// VirtualTable — 28px-row virtualized data grid for Eidollon entity browsers.
//
// Replaces the per-page plain `<table>` markup. Per CLAUDE.md Iron Rule 11,
// every entity screen consumes this; no bespoke result tables.
//
// Implementation: pure DOM windowing (no @tanstack/react-virtual yet — kept
// out so we don't add a runtime dep before the second consumer arrives).
// Renders only the visible row range plus an overscan; sticky header and
// sticky-left first column.
//
// Performance contract: smooth at 100k rows on a 2026 baseline laptop.
// Frame work bounded by overscan (default 12) + visible rows (~30 at 28px on
// a typical 900px canvas).

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useIsMobile } from './useBreakpoint.js';

export interface ColumnDef<R> {
  id: string;
  label: string;
  width: number | 'flex'; // px, or 'flex' for the one stretchy column
  align?: 'left' | 'right' | 'center';
  sticky?: 'left'; // first column only
  /** Row → cell content. Returning a string lets us put it in a `title=` attr. */
  render: (row: R) => ReactNode;
  /** Optional cell tone class (e.g. "rd" / "amb" for the urgency cell). */
  toneFor?: (row: R) => 'rd' | 'amb' | undefined;
  /** Allow sorting by this column. */
  sortable?: boolean;
  /** Server-side sort field id (defaults to ColumnDef.id). */
  sortField?: string;
  /** Mobile card layout (≤768px): promote this column to the card's title
   *  line. Falls back to the `flex` column, then the first non-sticky column,
   *  then column 0. Only the first `mobilePrimary` column wins. */
  mobilePrimary?: boolean;
  /** Mobile card layout: omit this column from the card entirely (title +
   *  meta). Use for purely decorative/wide columns that don't identify a row. */
  mobileHide?: boolean;
}

// Mobile card row height. Fits a title line + a meta line at a 44px-friendly
// tap target. Kept fixed so the existing windowing math still applies.
const MOBILE_CARD_HEIGHT = 66;
// Max secondary fields shown on the card's meta line before we stop (the rest
// live in the detail drawer the row taps open into).
const MOBILE_META_MAX = 4;

export interface SortState {
  field: string;
  dir: 'asc' | 'desc';
}

export interface VirtualTableProps<R> {
  rows: R[];
  columns: ColumnDef<R>[];
  rowKey: (row: R) => string;
  rowHeight?: number; // default 28
  overscan?: number; // default 12
  onRowClick?: (row: R) => void;
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /** Highlight rule (e.g. recompete-incumbent rows get amber tint). */
  rowTone?: (row: R) => 'selected' | 'watch' | undefined;
  /** Empty-state slot (rendered inside the table area when rows.length === 0). */
  emptyState?: ReactNode;
}

export function VirtualTable<R>({
  rows,
  columns,
  rowKey,
  rowHeight = 28,
  overscan = 12,
  onRowClick,
  sort,
  onSortChange,
  rowTone,
  emptyState,
}: VirtualTableProps<R>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight));
    ro.observe(el);
    setViewportH(el.clientHeight);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, []);

  const isMobile = useIsMobile();
  const effectiveRowHeight = isMobile ? MOBILE_CARD_HEIGHT : rowHeight;

  const totalH = rows.length * effectiveRowHeight;
  const startIdx = Math.max(0, Math.floor(scrollTop / effectiveRowHeight) - overscan);
  const endIdx = Math.min(
    rows.length,
    Math.ceil((scrollTop + viewportH) / effectiveRowHeight) + overscan,
  );
  const offsetY = startIdx * effectiveRowHeight;

  const visible = useMemo(() => rows.slice(startIdx, endIdx), [rows, startIdx, endIdx]);

  const gridTemplate = useMemo(
    () => columns.map((c) => (c.width === 'flex' ? 'minmax(0, 1fr)' : `${c.width}px`)).join(' '),
    [columns],
  );

  // Mobile card layout: pick the title column, then the secondary fields that
  // ride the meta line. Derived once from the column defs (Iron Rule 11 — the
  // page's columns drive both layouts; no bespoke mobile column list).
  const { primaryCol, metaCols } = useMemo(() => {
    const primary =
      columns.find((c) => c.mobilePrimary) ??
      columns.find((c) => c.width === 'flex') ??
      columns.find((c) => c.sticky !== 'left') ??
      columns[0];
    const meta = columns.filter((c) => c !== primary && !c.mobileHide).slice(0, MOBILE_META_MAX);
    return { primaryCol: primary, metaCols: meta };
  }, [columns]);

  const sortableCols = useMemo(() => columns.filter((c) => c.sortable), [columns]);

  const handleHeaderClick = (col: ColumnDef<R>) => {
    if (!col.sortable || !onSortChange) return;
    const field = col.sortField ?? col.id;
    if (sort?.field === field) {
      onSortChange({ field, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ field, dir: 'desc' });
    }
  };

  return (
    <div
      data-component="VirtualTable"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-pane)',
        position: 'relative',
      }}
    >
      {/* Mobile sort bar — cards carry no column headers, so sortable columns
          get a horizontally-scrollable chip row instead. Keeps sort reachable
          on touch (the desktop path sorts via header click). */}
      {isMobile && sortableCols.length > 0 && onSortChange && (
        <div data-component="VirtualTable.MobileSort" style={mobileSortBarStyle}>
          {sortableCols.map((col) => {
            const field = col.sortField ?? col.id;
            const active = sort?.field === field;
            return (
              <button
                key={col.id}
                type="button"
                onClick={() => handleHeaderClick(col)}
                style={{
                  ...mobileSortChipStyle,
                  ...(active ? { color: 'var(--fg-1)', borderColor: 'var(--primary)' } : {}),
                }}
              >
                {col.label.toUpperCase()}
                {active && <span style={{ marginLeft: 4 }}>{sort?.dir === 'asc' ? '↑' : '↓'}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Sticky header — desktop grid only. */}
      {!isMobile && (
        <div
          role="row"
          aria-rowindex={1}
          style={{
            ...headerRowStyle(rowHeight),
            gridTemplateColumns: gridTemplate,
          }}
        >
          {columns.map((col) => (
            <div
              key={col.id}
              role="columnheader"
              onClick={col.sortable ? () => handleHeaderClick(col) : undefined}
              aria-sort={
                sort?.field === (col.sortField ?? col.id)
                  ? sort.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
              }
              style={{
                ...headerCellStyle,
                ...(col.align
                  ? {
                      justifyContent:
                        col.align === 'right'
                          ? 'flex-end'
                          : col.align === 'center'
                            ? 'center'
                            : 'flex-start',
                    }
                  : {}),
                ...(col.sticky === 'left'
                  ? {
                      position: 'sticky',
                      left: 0,
                      background: 'var(--bg-pane)',
                      zIndex: 2,
                    }
                  : {}),
                cursor: col.sortable ? 'pointer' : 'default',
              }}
              title={col.sortable ? `sort by ${col.label}` : undefined}
            >
              <span>{col.label.toUpperCase()}</span>
              {sort?.field === (col.sortField ?? col.id) && (
                <span style={{ marginLeft: 4, color: 'var(--fg-2)' }}>
                  {sort.dir === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Scroll container */}
      <div
        ref={containerRef}
        role="rowgroup"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {rows.length === 0 ? (
          <div style={{ padding: 0 }}>{emptyState}</div>
        ) : (
          <div style={{ height: totalH, position: 'relative' }}>
            <div style={{ transform: `translateY(${offsetY}px)` }}>
              {visible.map((row, i) => {
                const absIdx = startIdx + i;
                const tone = rowTone?.(row);
                const rowBg =
                  tone === 'selected'
                    ? 'var(--bg-row-selected)'
                    : tone === 'watch'
                      ? 'var(--bg-row-watch)'
                      : 'transparent';
                if (isMobile && primaryCol) {
                  return (
                    <MobileCard
                      key={rowKey(row)}
                      absIdx={absIdx}
                      background={rowBg}
                      hasTone={tone != null}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      primaryCol={primaryCol}
                      metaCols={metaCols}
                      row={row}
                    />
                  );
                }
                return (
                  <div
                    key={rowKey(row)}
                    role="row"
                    aria-rowindex={absIdx + 2}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplate,
                      height: rowHeight,
                      alignItems: 'center',
                      cursor: onRowClick ? 'pointer' : 'default',
                      borderBottom: '1px solid var(--bd-soft, var(--bd))',
                      background: rowBg,
                    }}
                  >
                    {columns.map((col) => {
                      const content = col.render(row);
                      const cellTone = col.toneFor?.(row);
                      return (
                        <div
                          key={col.id}
                          role="cell"
                          style={{
                            ...cellStyle,
                            ...(col.align
                              ? {
                                  justifyContent:
                                    col.align === 'right'
                                      ? 'flex-end'
                                      : col.align === 'center'
                                        ? 'center'
                                        : 'flex-start',
                                  textAlign: col.align,
                                }
                              : {}),
                            ...(col.sticky === 'left'
                              ? {
                                  position: 'sticky',
                                  left: 0,
                                  background: tone ? 'transparent' : 'var(--bg-pane)',
                                  zIndex: 1,
                                }
                              : {}),
                            ...(cellTone
                              ? {
                                  color:
                                    cellTone === 'rd'
                                      ? 'var(--red)'
                                      : cellTone === 'amb'
                                        ? 'var(--amber)'
                                        : undefined,
                                }
                              : {}),
                          }}
                          title={typeof content === 'string' ? content : undefined}
                        >
                          {content}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const cellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  color: 'var(--fg-1)',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

function headerRowStyle(rowHeight: number): CSSProperties {
  return {
    display: 'grid',
    height: rowHeight,
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: 'var(--bg-pane)',
    borderBottom: '1px solid var(--bd)',
    flexShrink: 0,
  };
}

const headerCellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 0.4,
  color: 'var(--fg-3)',
  textTransform: 'uppercase',
  userSelect: 'none',
};

// ── Mobile card row (≤768px) ──────────────────────────────────────────────
// A row becomes a fixed-height card: a title line (the primary column) over a
// meta line of secondary label/value pairs. No horizontal scroll; the row's
// existing onRowClick still opens the detail drawer for the full record.
function MobileCard<R>({
  absIdx,
  background,
  onClick,
  primaryCol,
  metaCols,
  row,
}: {
  absIdx: number;
  background: string;
  hasTone: boolean;
  onClick?: () => void;
  primaryCol: ColumnDef<R>;
  metaCols: ColumnDef<R>[];
  row: R;
}) {
  const primaryContent = primaryCol.render(row);
  const primaryTone = primaryCol.toneFor?.(row);
  return (
    <div
      role="row"
      aria-rowindex={absIdx + 2}
      onClick={onClick}
      style={{
        height: MOBILE_CARD_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
        padding: '0 14px',
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: '1px solid var(--bd-soft, var(--bd))',
        background,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 13,
          lineHeight: '17px',
          color: toneColor(primaryTone) ?? 'var(--fg-1)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
        title={typeof primaryContent === 'string' ? primaryContent : undefined}
      >
        {primaryContent}
      </div>
      {metaCols.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {metaCols.map((col) => {
            const content = col.render(row);
            if (content == null || content === '') return null;
            const cellTone = col.toneFor?.(row);
            return (
              <span
                key={col.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 4,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 9,
                    letterSpacing: 0.4,
                    color: 'var(--fg-3)',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {col.label}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    color: toneColor(cellTone) ?? 'var(--fg-2)',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {content}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function toneColor(t: 'rd' | 'amb' | undefined): string | undefined {
  return t === 'rd' ? 'var(--red)' : t === 'amb' ? 'var(--amber)' : undefined;
}

const mobileSortBarStyle: CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  height: 40,
  flexShrink: 0,
  padding: '0 12px',
  overflowX: 'auto',
  background: 'var(--bg-pane)',
  borderBottom: '1px solid var(--bd)',
  scrollbarWidth: 'none',
};

const mobileSortChipStyle: CSSProperties = {
  flexShrink: 0,
  height: 28,
  padding: '0 12px',
  background: 'var(--bg-pane-2, transparent)',
  border: '1px solid var(--bd-strong, var(--bd))',
  borderRadius: 14,
  color: 'var(--fg-3)',
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 0.4,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
